import React, { useEffect, useMemo, useRef, useState } from "react";

/**
 * EliteKanbanBoardV2_7_2.jsx — React 17 (no external libs)
 * Fixes from your notes:
 * - Job Card shows batch qty vs total; adds Batches panel
 * - Larger fonts + touch targets, no visual overlap (responsive grids)
 * - Block Planning → Paused; QC → Paused allowed
 * - Reason optional (not enforced) for scheduling/rescheduling
 * - Batch badge counts siblings across ALL lanes (no 1/1 weirdness)
 * - Card buttons aligned & colored; inputs visibly styled
 */

//
// ────────────────────────────────────────────────────────────────────────────
// Constants & helpers
// ────────────────────────────────────────────────────────────────────────────
const STATUSES = [
  { key: "Planned", label: "Up / Planning" },
  { key: "Scheduled", label: "Scheduled" },
  { key: "In_Production", label: "In Production" },
  { key: "QC", label: "QC" },
  { key: "Completed", label: "Completed" },
  { key: "Paused", label: "Paused / Hold" },
];
const STATUS_INDEX = STATUSES.reduce((m, s, i) => ((m[s.key] = i), m), {});
const DATE_FMT = (d) => (d ? new Date(d).toLocaleDateString("en-CA") : "–"); // yyyy-mm-dd
const todayYMD = () => new Date().toISOString().slice(0, 10);
const toISOStart = (ymd) =>
  ymd && ymd.length === 10 ? `${ymd}T00:00:00` : ymd || "";
const toISOEnd = (ymd) =>
  ymd && ymd.length === 10 ? `${ymd}T23:59:59` : ymd || "";
function daysLate(ymd) {
  if (!ymd) return 0;
  const a = new Date(`${ymd}T00:00:00`);
  const b = new Date(`${todayYMD()}T00:00:00`);
  return Math.max(0, Math.floor((b - a) / 86400000));
}

const DEFAULT_DAY_CAPACITY_MIN = 480;
const DEFAULT_CARD_MIN = 60;

const DEPT_MATCHERS = {
  Screening: [/screen ?print/i, /pad ?print/i, /silk/i],
  Embroidery: [/embroider/i],
  "Direct to Film": [/\bDTF\b/i, /direct\s*to\s*film/i],
  Engraving: [/engrave/i, /laser/i],
  Cresting: [/crest/i, /heat ?press/i, /vinyl/i],
  Awards: [/award/i, /trophy/i],
};
const CHARGE_ID_TO_DEPT = {
  15: "Embroidery",
  45: "Screening",
  107: "Direct to Film",
};
function inferDepartmentForDeco(deco) {
  if (!deco) return "";
  if (deco.chargeId != null && CHARGE_ID_TO_DEPT[deco.chargeId])
    return CHARGE_ID_TO_DEPT[deco.chargeId];
  const name = String(deco.chargeName || "");
  for (const [dept, tests] of Object.entries(DEPT_MATCHERS))
    if (tests.some((re) => re.test(name))) return dept;
  return "";
}
const money = (n) => (typeof n === "number" ? n.toFixed(2) : "–");
const clampInt = (v, min, max) =>
  Math.max(min, Math.min(max, parseInt(v || 0, 10)));

//
// ────────────────────────────────────────────────────────────────────────────
// Sweet (inline) — alert / confirm / prompt
// ────────────────────────────────────────────────────────────────────────────
function SweetDialog({
  open,
  type = "info",
  title,
  content,
  confirmText = "OK",
  cancelText = "Cancel",
  showCancel = false,
  input = false,
  inputProps = {},
  onClose,
}) {
  const [ival, setIval] = React.useState(inputProps?.defaultValue || "");
  React.useEffect(() => {
    setIval(inputProps?.defaultValue || "");
  }, [open, inputProps?.defaultValue]);
  if (!open) return null;

  const icon =
    type === "success"
      ? "✓"
      : type === "error"
      ? "✕"
      : type === "warn"
      ? "!"
      : "i";
  const iconBg =
    type === "success"
      ? "#dcfce7"
      : type === "error"
      ? "#fee2e2"
      : type === "warn"
      ? "#fef3c7"
      : "#e5f0ff";
  const iconCol =
    type === "success"
      ? "#166534"
      : type === "error"
      ? "#991b1b"
      : type === "warn"
      ? "#92400e"
      : "#175cd3";

  const handleConfirm = () => {
    if (input && inputProps?.required && !ival.trim()) return;
    onClose?.({ ok: true, value: input ? ival : undefined });
  };
  const handleCancel = () => onClose?.({ ok: false });

  return (
    <div
      style={modal.overlay}
      onClick={handleCancel}
      role="dialog"
      aria-modal="true"
    >
      <div
        style={{ ...modal.card, width: "min(560px, 96vw)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 999,
              background: iconBg,
              color: iconCol,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 700,
            }}
          >
            {icon}
          </div>
          <h3 style={{ margin: 0, fontSize: 18 }}>{title || "Notice"}</h3>
        </div>
        <div style={{ marginTop: 12, display: "grid", gap: 10, fontSize: 15 }}>
          {typeof content === "string" ? (
            <p style={{ margin: 0 }}>{content}</p>
          ) : (
            content
          )}
          {input && (
            <input
              autoFocus
              type={inputProps.type || "text"}
              placeholder={inputProps.placeholder || ""}
              value={ival}
              onChange={(e) => setIval(e.target.value)}
              style={ui.input}
            />
          )}
        </div>
        <div
          style={{
            display: "flex",
            gap: 10,
            justifyContent: showCancel ? "flex-end" : "center",
            marginTop: 18,
          }}
        >
          {showCancel && (
            <button style={ui.btnGhost} onClick={handleCancel}>
              {cancelText}
            </button>
          )}
          <button style={ui.btnPrimary} onClick={handleConfirm}>
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

function useSweet() {
  const [dlg, setDlg] = useState({ open: false });
  const api = useRef({});
  api.current.alert = ({
    type = "info",
    title = "Notice",
    content,
    confirmText = "OK",
  } = {}) =>
    new Promise((resolve) =>
      setDlg({
        open: true,
        type,
        title,
        content,
        confirmText,
        showCancel: false,
        onClose: (res) => {
          setDlg({ open: false });
          resolve(!!res?.ok);
        },
      })
    );
  api.current.confirm = ({
    type = "warn",
    title = "Are you sure?",
    content,
    confirmText = "Yes",
    cancelText = "No",
  } = {}) =>
    new Promise((resolve) =>
      setDlg({
        open: true,
        type,
        title,
        content,
        confirmText,
        cancelText,
        showCancel: true,
        onClose: (res) => {
          setDlg({ open: false });
          resolve(!!res?.ok);
        },
      })
    );
  api.current.prompt = ({
    type = "info",
    title = "Input",
    content,
    placeholder = "",
    defaultValue = "",
    required = false,
    confirmText = "OK",
    cancelText = "Cancel",
  } = {}) =>
    new Promise((resolve) =>
      setDlg({
        open: true,
        type,
        title,
        content,
        confirmText,
        cancelText,
        showCancel: true,
        input: true,
        inputProps: { placeholder, defaultValue, required },
        onClose: (res) => {
          setDlg({ open: false });
          resolve(res?.ok ? res.value ?? "" : null);
        },
      })
    );
  const node = (
    <SweetDialog
      {...dlg}
      onClose={dlg.onClose || (() => setDlg({ open: false }))}
    />
  );
  return [api.current, node];
}

//
// ────────────────────────────────────────────────────────────────────────────
// Reusable little UI bits
// ────────────────────────────────────────────────────────────────────────────
function Section({ title, children }) {
  return (
    <div style={ui.section}>
      <div
        style={{ ...ui.subtle, marginBottom: 8, fontWeight: 700, fontSize: 15 }}
      >
        {title}
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
          gap: 12,
        }}
      >
        {children}
      </div>
    </div>
  );
}
function Row({ l, v }) {
  return (
    <div>
      <div style={ui.label}>{l}</div>
      <div style={ui.value}>{v || v === 0 ? v : "–"}</div>
    </div>
  );
}
function RowChip({ l, list }) {
  return (
    <div>
      <div style={ui.label}>{l}</div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {(list || []).length ? (
          list.map((x, i) => (
            <span key={i} style={ui.chip}>
              {x}
            </span>
          ))
        ) : (
          <span style={ui.value}>–</span>
        )}
      </div>
    </div>
  );
}
function Input({ l, value, onChange, type = "text", readOnly, placeholder }) {
  return (
    <label style={ui.inputWrap}>
      <span style={ui.inputLabel}>{l}</span>
      <input
        type={type}
        placeholder={placeholder}
        value={value ?? ""}
        readOnly={readOnly}
        onChange={(e) => onChange?.(e.target.value)}
        style={ui.input}
      />
    </label>
  );
}
function Select({ l, value, onChange, options = [] }) {
  return (
    <label style={ui.inputWrap}>
      <span style={ui.inputLabel}>{l}</span>
      <select
        value={value || ""}
        onChange={(e) => onChange?.(e.target.value)}
        style={ui.input}
      >
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </label>
  );
}
const updateAt = (set, i, v) =>
  set((list) => list.map((x, idx) => (idx === i ? v : x)));
const removeAt = (set, i) => set((list) => list.filter((_, idx) => idx !== i));

//
// ────────────────────────────────────────────────────────────────────────────
// Job Card (fixed-size, tabs). Shows batch qty and siblings across lanes.
// ────────────────────────────────────────────────────────────────────────────
function JobCardModal({
  open,
  onClose,
  card,
  onPatchCard,
  onCancelCard,
  onReinstateCard,
  siblingsAll = [],
  siblingsSameLane = [],
  onOpenSplit,
  onOpenJoin,
}) {
  const [tab, setTab] = useState("overview");
  const [processes, setProcesses] = useState(card?.processes || []);
  const [bom, setBOM] = useState(card?.bom || []);
  const [chat, setChat] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [chatText, setChatText] = useState("");

  useEffect(() => {
    if (!open || !card) return;
    setTab("overview");
    setProcesses(card.processes || []);
    setBOM(card.bom || []);
    (async () => {
      if (!card.__isStep) {
        setChat([]);
        return;
      }
      setChatLoading(true);
      try {
        const r = await fetch(
          `/production/steps/${encodeURIComponent(card.id)}/messages`
        );
        if (r.ok) {
          const js = await r.json();
          setChat(Array.isArray(js?.data) ? js.data : []);
        } else setChat([]);
      } catch {
        setChat([]);
      } finally {
        setChatLoading(false);
      }
    })();
  }, [open, card]);

  if (!open || !card) return null;
  const o = card.raw?.order || {};
  const it = card.raw?.item || {};
  const d = card.raw?.deco || {};

  // Qty logic: if batch/rootId, show "batch / total"
  const totalAcross =
    siblingsAll.reduce((t, c) => t + (Number(c.qtyPlanned || 0) || 0), 0) ||
    card.rootQty ||
    (card.raw?.item?.quantity ?? card.qtyPlanned ?? 0);
  const isBatch = !!card.rootId || siblingsAll.length > 1;
  const qtyDisplay = isBatch
    ? `${card.qtyPlanned ?? 0} / ${totalAcross}`
    : card.qtyPlanned ?? it.quantity ?? 0;

  const addProcess = () =>
    setProcesses((p) => [
      ...p,
      {
        id: `proc-${Date.now()}`,
        name: "",
        owner: "",
        type: "Manual",
        machine: "",
        estMinutes: "",
        dueDate: "",
      },
    ]);
  const addBOM = () =>
    setBOM((b) => [
      ...b,
      {
        id: `bom-${Date.now()}`,
        part: "",
        desc: "",
        qty: "",
        unit: "",
        supplier: "",
      },
    ]);

  const saveMeta = async () => {
    try {
      if (card.__isStep) {
        await fetch(
          `/production/steps/${encodeURIComponent(card.id)}/processes`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(processes),
          }
        );
        await fetch(`/production/steps/${encodeURIComponent(card.id)}/bom`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(bom),
        });
      }
      onPatchCard(card.id, { processes, bom });
      onClose();
    } catch {
      onPatchCard(card.id, { processes, bom });
      onClose();
    }
  };

  const sendChat = async () => {
    const text = chatText.trim();
    if (!text) return;
    setChatText("");
    const localMsg = {
      id: `local-${Date.now()}`,
      text,
      when: new Date().toISOString(),
      author: "You",
    };
    setChat((c) => [...c, localMsg]);
    try {
      if (card.__isStep)
        await fetch(
          `/production/steps/${encodeURIComponent(card.id)}/messages`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ text }),
          }
        );
    } catch {}
  };

  const hasSibs = siblingsAll.length > 1;
  const groupBadge = hasSibs
    ? `Batch ${(card.batchIndex ?? 0) + 1}/${siblingsAll.length}`
    : null;

  return (
    <div style={modal.overlay} onClick={onClose}>
      <div style={{ ...modal.cardFixed }} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={modal.header}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            <h3 style={{ margin: 0, fontSize: 20 }}>
              {card.orderNumber || card.title || "Job Card"}
            </h3>
            {card.cancelled && <span style={ui.cancelBadge}>CANCELLED</span>}
            {card.status && (
              <span style={ui.statusPill}>
                {card.status.replace(/_/g, " ")}
              </span>
            )}
            {groupBadge && <span style={ui.badge}>{groupBadge}</span>}
          </div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            {card.status === "Scheduled" && !card.cancelled && (
              <button style={ui.btnIndigo} onClick={() => onOpenSplit(card)}>
                Split…
              </button>
            )}
            {hasSibs && !card.cancelled && (
              <button style={ui.btnGhost} onClick={() => onOpenJoin(card)}>
                Join…
              </button>
            )}
            {!card.cancelled ? (
              <button
                style={ui.btnDanger}
                onClick={() => onCancelCard(card, "Cancelled from Job Card")}
              >
                Cancel
              </button>
            ) : (
              <button
                style={ui.btnWarn}
                onClick={() => onReinstateCard(card, "")}
              >
                Reinstate
              </button>
            )}
            <button style={ui.btnGhost} onClick={onClose}>
              Close
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div style={tabs.wrap}>
          {["overview", "processes", "bom", "chat", "activity"].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              style={{ ...tabs.tab, ...(tab === t ? tabs.active : null) }}
            >
              {t === "overview" ? "Details" : t[0].toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>

        {/* Content */}
        <div style={modal.content}>
          {tab === "overview" && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
                gap: 12,
              }}
            >
              <Section title="Project Information">
                <Row l="Project name" v={o.projectName} />
                <Row l="Project Number" v={o.orderNumber || card.orderNumber} />
                <RowChip
                  l="PO Numbers"
                  list={Array.isArray(o.poNumbers) ? o.poNumbers : []}
                />
              </Section>

              <Section title="Product information">
                <Row l="SKU" v={it.productId || card.sku} />
                <Row l="Sizes" v={(it.sizes || []).join(", ")} />
                <Row l="Colors" v={(it.colors || []).join(", ")} />
                <Row l="Net cost goods" v={money(it.netCostGoods)} />
                <Row l="Retail cost goods" v={money(it.retailCostGoods)} />
                <Row l="Supplier information" v={it.supplierName} />
                <Row l="Category" v={it.category} />
              </Section>

              <Section title="Decoration information">
                <Row
                  l="Deco department"
                  v={
                    card.claimedDept ||
                    card.department ||
                    inferDepartmentForDeco(d)
                  }
                />
                <Row l="Matrix pricing" v={d.chargeName || card.matrix} />
                <Row l="Net cost goods" v={money(d.netCostGoods)} />
                <Row l="Retail cost goods" v={money(d.retailCostGoods)} />
                <Row l="Location" v={d.location || card.location} />
              </Section>

              <Section title="Art information">
                {d.artProofUrl ? (
                  <a
                    href={d.artProofUrl}
                    target="_blank"
                    rel="noreferrer"
                    style={{ textDecoration: "underline" }}
                  >
                    Open proof
                  </a>
                ) : (
                  <div style={ui.value}>–</div>
                )}
              </Section>

              <div style={{ gridColumn: "1/-1" }}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                    gap: 12,
                  }}
                >
                  <Row l="Customer" v={o.customerName || card.customer} />
                  <Row l="Qty (this / total)" v={qtyDisplay} />
                  <Row
                    l="In-hands"
                    v={DATE_FMT(o.inHandsDate || card.dueDate)}
                  />
                  <Row
                    l="Scheduled Date"
                    v={card.plannedStart ? DATE_FMT(card.plannedStart) : "–"}
                  />
                  <Row
                    l="Machine"
                    v={card.machine || (hasSibs ? "Multi" : "–")}
                  />
                </div>
              </div>

              {hasSibs && (
                <div style={{ gridColumn: "1/-1" }}>
                  <div
                    style={{ ...ui.subtle, marginBottom: 6, fontWeight: 700 }}
                  >
                    Batches
                  </div>
                  <div
                    style={{
                      border: "1px solid #eee",
                      borderRadius: 8,
                      overflow: "hidden",
                    }}
                  >
                    <table
                      style={{
                        width: "100%",
                        borderCollapse: "collapse",
                        fontSize: 15,
                      }}
                    >
                      <thead>
                        <tr style={{ background: "#f9fafb" }}>
                          <th style={th}>ID</th>
                          <th style={th}>Lane</th>
                          <th style={th}>Qty</th>
                          <th style={th}>Machine</th>
                          <th style={th}>Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {siblingsAll.map((s) => (
                          <tr
                            key={s.id}
                            style={{ borderTop: "1px solid #eee" }}
                          >
                            <td style={td}>
                              {s.id === card.id ? <b>(This)</b> : s.id}
                            </td>
                            <td style={td}>
                              {s.status?.replace(/_/g, " ") || "—"}
                            </td>
                            <td style={td}>{s.qtyPlanned ?? 0}</td>
                            <td style={td}>{s.machine || "—"}</td>
                            <td style={td}>
                              {s.plannedStart ? DATE_FMT(s.plannedStart) : "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {tab === "processes" && (
            <div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <strong>Processes</strong>
                <button style={ui.btnLink} onClick={addProcess}>
                  + Add process
                </button>
              </div>
              <div style={{ marginTop: 10, display: "grid", gap: 10 }}>
                {(processes || []).map((p, idx) => (
                  <div key={p.id} style={ui.rowCard}>
                    <Input l="#" value={String(idx + 1)} readOnly />
                    <Input
                      l="Name"
                      value={p.name}
                      onChange={(v) =>
                        updateAt(setProcesses, idx, { ...p, name: v })
                      }
                    />
                    <Input
                      l="Person"
                      value={p.owner}
                      onChange={(v) =>
                        updateAt(setProcesses, idx, { ...p, owner: v })
                      }
                    />
                    <Select
                      l="Type"
                      value={p.type || "Manual"}
                      onChange={(v) =>
                        updateAt(setProcesses, idx, { ...p, type: v })
                      }
                      options={["Manual", "Machine"]}
                    />
                    <Input
                      l="Machine (opt)"
                      value={p.machine || ""}
                      onChange={(v) =>
                        updateAt(setProcesses, idx, { ...p, machine: v })
                      }
                    />
                    <Input
                      l="Est. minutes"
                      type="number"
                      value={p.estMinutes || ""}
                      onChange={(v) =>
                        updateAt(setProcesses, idx, { ...p, estMinutes: v })
                      }
                    />
                    <Input
                      l="Due date"
                      type="date"
                      value={p.dueDate || ""}
                      onChange={(v) =>
                        updateAt(setProcesses, idx, { ...p, dueDate: v })
                      }
                    />
                    <button
                      className="danger"
                      style={ui.btnDanger}
                      onClick={() => removeAt(setProcesses, idx)}
                    >
                      Delete
                    </button>
                  </div>
                ))}
                {!processes?.length && (
                  <div style={ui.empty}>No processes yet.</div>
                )}
              </div>
            </div>
          )}

          {tab === "bom" && (
            <div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <strong>BOM</strong>
                <button style={ui.btnLink} onClick={addBOM}>
                  + Add line
                </button>
              </div>
              <div style={{ marginTop: 10, display: "grid", gap: 10 }}>
                {(bom || []).map((b, idx) => (
                  <div key={b.id} style={ui.rowCard}>
                    <Input
                      l="Part #"
                      value={b.part}
                      onChange={(v) => updateAt(setBOM, idx, { ...b, part: v })}
                    />
                    <Input
                      l="Description"
                      value={b.desc}
                      onChange={(v) => updateAt(setBOM, idx, { ...b, desc: v })}
                    />
                    <Input
                      l="Qty"
                      type="number"
                      value={b.qty}
                      onChange={(v) => updateAt(setBOM, idx, { ...b, qty: v })}
                    />
                    <Input
                      l="Unit"
                      value={b.unit}
                      onChange={(v) => updateAt(setBOM, idx, { ...b, unit: v })}
                    />
                    <Input
                      l="Supplier"
                      value={b.supplier}
                      onChange={(v) =>
                        updateAt(setBOM, idx, { ...b, supplier: v })
                      }
                    />
                    <button
                      className="danger"
                      style={ui.btnDanger}
                      onClick={() => removeAt(setBOM, idx)}
                    >
                      Delete
                    </button>
                  </div>
                ))}
                {!bom?.length && <div style={ui.empty}>No BOM lines yet.</div>}
              </div>
            </div>
          )}

          {tab === "chat" && (
            <div style={{ display: "grid", gap: 10 }}>
              <div style={ui.subtle}>Order / Production Chat</div>
              <div style={chatStyles.box}>
                {chatLoading ? (
                  <div style={ui.subtle}>Loading…</div>
                ) : (chat || []).length ? (
                  (chat || []).map((m) => (
                    <div key={m.id} style={chatStyles.msg}>
                      <div style={chatStyles.meta}>
                        <b>{m.author || "User"}</b> •{" "}
                        {new Date(m.when || Date.now()).toLocaleString()}
                      </div>
                      <div>{m.text}</div>
                    </div>
                  ))
                ) : (
                  <div style={ui.empty}>No messages yet.</div>
                )}
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                <input
                  placeholder="Type a message…"
                  value={chatText}
                  onChange={(e) => setChatText(e.target.value)}
                  style={ui.input}
                />
                <button style={ui.btnPrimary} onClick={sendChat}>
                  Send
                </button>
              </div>
            </div>
          )}

          {tab === "activity" && (
            <div>
              <div style={ui.subtle}>Lifecycle</div>
              <ol style={{ margin: 0, paddingLeft: 18, fontSize: 15 }}>
                {(card.lifecycle || []).length ? (
                  card.lifecycle.map((ev, i) => (
                    <li key={i}>
                      <b>{ev.when || ""}</b>: {ev.text || ""}
                    </li>
                  ))
                ) : (
                  <li>–</li>
                )}
              </ol>
            </div>
          )}
        </div>

        <div style={modal.footer}>
          <button style={ui.btnPrimary} onClick={saveMeta}>
            Save
          </button>
          <button style={ui.btnGhost} onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

//
// ────────────────────────────────────────────────────────────────────────────
// Gate to Scheduled (reason optional now)
// ────────────────────────────────────────────────────────────────────────────
function MoveGateModal({ to, fields, onCancel, onConfirm }) {
  const [form, setForm] = useState(
    fields || {
      operator: "",
      machine: "",
      plannedStart: "",
      plannedFinish: "",
      reason: "",
    }
  );
  const req = to === "Scheduled";
  const disabled =
    req &&
    (!form.operator ||
      !form.machine ||
      !form.plannedStart ||
      !form.plannedFinish);
  return (
    <div style={modal.overlay}>
      <div style={{ ...modal.card, width: "min(760px,96vw)" }}>
        <div style={modal.header}>
          <h3 style={{ margin: 0, fontSize: 20 }}>Move to {to}</h3>
          <button onClick={onCancel} style={ui.btnGhost}>
            Cancel
          </button>
        </div>
        <div
          style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}
        >
          <Input
            l="Operator *"
            value={form.operator}
            onChange={(v) => setForm({ ...form, operator: v })}
            placeholder="Who runs it"
          />
          <Input
            l="Machine *"
            value={form.machine}
            onChange={(v) => setForm({ ...form, machine: v })}
            placeholder="Machine name/code"
          />
          <Input
            l="Planned Start (day) *"
            type="date"
            value={form.plannedStart}
            onChange={(v) => setForm({ ...form, plannedStart: v })}
          />
          <Input
            l="Planned Finish (day) *"
            type="date"
            value={form.plannedFinish}
            onChange={(v) => setForm({ ...form, plannedFinish: v })}
          />
          <Input
            l="Reason (optional)"
            value={form.reason}
            onChange={(v) => setForm({ ...form, reason: v })}
            placeholder="Optional note"
          />
        </div>
        <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
          <button
            disabled={disabled}
            onClick={() => onConfirm(form)}
            style={{ ...ui.btnPrimary, opacity: disabled ? 0.6 : 1 }}
          >
            Confirm Move
          </button>
        </div>
      </div>
    </div>
  );
}

//
// ────────────────────────────────────────────────────────────────────────────
// Partial move (In_Production → QC/Paused etc.)
// ────────────────────────────────────────────────────────────────────────────
function PartialMoveModal({
  open,
  from,
  defaultTo = "QC",
  card,
  onCancel,
  onConfirm,
}) {
  const [qty, setQty] = useState("");
  const [to, setTo] = useState(defaultTo);
  if (!open || !card) return null;
  const max = Math.max(
    0,
    Number(card.qtyPlanned || 0) - Number(card.qtyDone || 0)
  );
  const options =
    from === "In_Production"
      ? ["QC", "Paused"]
      : ["Scheduled", "In_Production", "QC", "Paused"];
  return (
    <div style={modal.overlay} onClick={onCancel}>
      <div style={modal.card} onClick={(e) => e.stopPropagation()}>
        <div style={modal.header}>
          <h3 style={{ margin: 0, fontSize: 20 }}>
            Partial move from {from.replace(/_/g, " ")}
          </h3>
          <button style={ui.btnGhost} onClick={onCancel}>
            Close
          </button>
        </div>
        <div style={{ display: "grid", gap: 12 }}>
          <div style={ui.subtle}>Available qty: {max}</div>
          <Input
            l="Quantity to move *"
            type="number"
            value={qty}
            onChange={(v) => setQty(v)}
          />
          <Select
            l="Destination *"
            value={to}
            onChange={(v) => setTo(v)}
            options={options}
          />
        </div>
        <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
          <button
            style={ui.btnPrimary}
            onClick={() => onConfirm({ qty: Number(qty || 0), to })}
          >
            Confirm partial move
          </button>
          <button style={ui.btnGhost} onClick={onCancel}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
function createBatchFrom(card, qty, to) {
  const rootId = card.rootId || card.id;
  const batchId = `${rootId}#b${Date.now() % 100000}`;
  const parentQty = Number(card.qtyPlanned || 0);
  const doneQty = Number(card.qtyDone || 0);
  const remaining = Math.max(0, parentQty - doneQty);
  if (qty < 1 || qty > remaining) return null;
  const originalQty = card.rootQty || parentQty;
  const child = {
    ...card,
    id: batchId,
    rootId,
    rootQty: originalQty,
    batchOf: card.id,
    qtyPlanned: qty,
    qtyDone: to === "QC" && card.status === "In_Production" ? qty : 0,
    status: to,
    lifecycle: [
      ...(card.lifecycle || []),
      {
        when: new Date().toLocaleString(),
        text: `Partial move ${qty} → ${to.replace(/_/g, " ")}`,
      },
    ],
  };
  const parentAfter = {
    ...card,
    qtyPlanned: parentQty - qty,
    lifecycle: [
      ...(card.lifecycle || []),
      {
        when: new Date().toLocaleString(),
        text: `Split out ${qty} to ${to.replace(/_/g, " ")}`,
      },
    ],
  };
  return { child, parentAfter };
}

//
// ────────────────────────────────────────────────────────────────────────────
// Split & Join wizards (Scheduled)
// ────────────────────────────────────────────────────────────────────────────
function SplitWizard({ open, card, onCancel, onConfirm }) {
  const [rows, setRows] = useState([
    { id: 1, qty: "", machine: "", date: card?.plannedStart || todayYMD() },
  ]);
  useEffect(() => {
    if (open)
      setRows([
        { id: 1, qty: "", machine: "", date: card?.plannedStart || todayYMD() },
      ]);
  }, [open, card]);
  if (!open || !card) return null;

  const available = Math.max(
    0,
    Number(card.qtyPlanned || 0) - Number(card.qtyDone || 0)
  );
  const total = rows.reduce((t, r) => t + (parseInt(r.qty || 0, 10) || 0), 0);
  const over = total > available;

  const addRow = () =>
    setRows((rs) => [
      ...rs,
      {
        id: Date.now(),
        qty: "",
        machine: "",
        date: card.plannedStart || todayYMD(),
      },
    ]);
  const update = (i, patch) =>
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const remove = (i) =>
    setRows((rs) => (rs.length > 1 ? rs.filter((_, idx) => idx !== i) : rs));

  return (
    <div style={modal.overlay} onClick={onCancel}>
      <div
        style={{ ...modal.card, width: "min(780px,96vw)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={modal.header}>
          <h3 style={{ margin: 0, fontSize: 20 }}>
            Split into batches (Scheduled)
          </h3>
          <button style={ui.btnGhost} onClick={onCancel}>
            Close
          </button>
        </div>

        <div style={{ display: "grid", gap: 10 }}>
          <div style={ui.subtle}>
            Available qty: <b>{available}</b> • Allocate across machines/dates
            below.
          </div>
          <div style={{ display: "grid", gap: 8 }}>
            {rows.map((r, idx) => (
              <div key={r.id} style={ui.rowCard}>
                <Input
                  l="Qty"
                  type="number"
                  value={r.qty}
                  onChange={(v) => update(idx, { qty: v })}
                />
                <Input
                  l="Machine"
                  value={r.machine}
                  onChange={(v) => update(idx, { machine: v })}
                />
                <Input
                  l="Date"
                  type="date"
                  value={r.date}
                  onChange={(v) => update(idx, { date: v })}
                />
                <button style={ui.btnDanger} onClick={() => remove(idx)}>
                  Remove
                </button>
              </div>
            ))}
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button style={ui.btnLink} onClick={addRow}>
              + Add batch
            </button>
            <div
              style={{
                marginLeft: "auto",
                color: over ? "#991b1b" : "#111827",
              }}
            >
              Total: {total} {over && "• Over allocation!"}
            </div>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            gap: 10,
            justifyContent: "flex-end",
            marginTop: 12,
          }}
        >
          <button
            style={ui.btnPrimary}
            disabled={!total || over}
            onClick={() => onConfirm(rows)}
          >
            Create batches
          </button>
        </div>
      </div>
    </div>
  );
}
function JoinWizard({ open, card, siblings = [], onCancel, onConfirm }) {
  const [selected, setSelected] = useState(new Set());
  useEffect(() => {
    if (open) {
      const s = new Set();
      siblings.forEach((sib) => {
        if (sib.id !== card.id) s.add(sib.id);
      });
      setSelected(s);
    }
  }, [open, card, siblings]);
  if (!open || !card) return null;
  const toggle = (id) =>
    setSelected((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  const chosen = siblings.filter((s) => selected.has(s.id));
  return (
    <div style={modal.overlay} onClick={onCancel}>
      <div
        style={{ ...modal.card, width: "min(760px,96vw)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={modal.header}>
          <h3 style={{ margin: 0, fontSize: 20 }}>Join batches</h3>
          <button style={ui.btnGhost} onClick={onCancel}>
            Close
          </button>
        </div>
        <div style={{ display: "grid", gap: 10 }}>
          <div style={ui.subtle}>
            Pick sibling batches (same order & lane) to merge into this card.
          </div>
          <div style={{ border: "1px solid #eee", borderRadius: 8 }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: 15,
              }}
            >
              <thead>
                <tr style={{ background: "#f9fafb" }}>
                  <th style={th}>Select</th>
                  <th style={th}>Card</th>
                  <th style={th}>Qty</th>
                  <th style={th}>Machine</th>
                  <th style={th}>Date</th>
                </tr>
              </thead>
              <tbody>
                {siblings.map((s) => (
                  <tr key={s.id} style={{ borderTop: "1px solid #eee" }}>
                    <td style={td}>
                      <input
                        type="checkbox"
                        disabled={s.id === card.id}
                        checked={selected.has(s.id)}
                        onChange={() => toggle(s.id)}
                      />
                    </td>
                    <td style={td}>
                      {s.id === card.id ? <b>(Target)</b> : s.id}
                    </td>
                    <td style={td}>{s.qtyPlanned ?? 0}</td>
                    <td style={td}>
                      {s.machine || (s.rootId ? "Multi" : "—")}
                    </td>
                    <td style={td}>
                      {s.plannedStart ? DATE_FMT(s.plannedStart) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={ui.subtle}>
            Selected:{" "}
            {chosen.reduce((t, c) => t + (Number(c.qtyPlanned || 0) || 0), 0)}{" "}
            items
          </div>
        </div>
        <div
          style={{
            display: "flex",
            gap: 10,
            justifyContent: "flex-end",
            marginTop: 12,
          }}
        >
          <button
            style={ui.btnPrimary}
            disabled={!selected.size}
            onClick={() => onConfirm(Array.from(selected))}
          >
            Merge
          </button>
        </div>
      </div>
    </div>
  );
}
const th = {
  textAlign: "left",
  padding: "10px 12px",
  fontSize: 13,
  color: "#555",
};
const td = { padding: "10px 12px", fontSize: 15 };

//
// ────────────────────────────────────────────────────────────────────────────
// Main Board
// ────────────────────────────────────────────────────────────────────────────
export default function EliteKanbanBoardV2_7_2() {
  const sectionRefs = useRef(
    Object.fromEntries(STATUSES.map((s) => [s.key, React.createRef()]))
  ).current;
  const colRectsRef = useRef({});
  const dragCalcRaf = useRef(0);
  const pendingPointer = useRef({ key: null, y: 0 });
  const [columns, setColumns] = useState(() => emptyColumns());
  const [error, setError] = useState("");
  const [planningPage, setPlanningPage] = useState(1);
  const [planningHasMore, setPlanningHasMore] = useState(true);
  const [planningLoading, setPlanningLoading] = useState(false);

  const [dnd, setDnd] = useState({
    active: false,
    from: null,
    lastMain: null,
    allowed: new Set(),
    overCol: null,
    overIndex: -1,
  });

  // Popups
  const [modalOpen, setModalOpen] = useState(false);
  const [modalCard, setModalCard] = useState(null);
  const [moveModal, setMoveModal] = useState({
    open: false,
    to: null,
    card: null,
    fields: {
      operator: "",
      machine: "",
      plannedStart: "",
      plannedFinish: "",
      reason: "",
    },
  });
  const [partialMove, setPartialMove] = useState({
    open: false,
    from: null,
    card: null,
    defaultTo: "QC",
  });
  const [splitUI, setSplitUI] = useState({ open: false, card: null });
  const [joinUI, setJoinUI] = useState({ open: false, card: null });

  // Sweet
  const [sweet, sweetNode] = useSweet();

  // DnD & scroll
  const listRefs = useRef(
    Object.fromEntries(STATUSES.map((s) => [s.key, React.createRef()]))
  ).current;
  const boardRef = useRef(null);
  const dragRef = useRef({
    cardId: null,
    from: null,
    fromIndex: -1,
    overIndex: -1,
    overCol: null,
    lastMain: null,
    cardHeight: 0,
  });

  // Floating drag preview (Trello-like)
  const dragPreviewRef = useRef({ el: null });
  function setDragPreviewPos(x, y) {
    const p = dragPreviewRef.current;
    if (!p.el) return;
    p.el.style.transform = `translate(${x + 12}px, ${y + 12}px)`; // offset from cursor
  }

  // function handleBoardDragOver(e) {
  //   e.preventDefault();
  //   if (!boardRef.current) return;
  //   const boardEl = boardRef.current;

  //   // Horizontal autoscroll near board edges
  //   const brect = boardEl.getBoundingClientRect();
  //   const HTH = 80, HSTEP = 24;
  //   if (e.clientX < brect.left + HTH) boardEl.scrollLeft -= HSTEP;
  //   else if (e.clientX > brect.right - HTH) boardEl.scrollLeft += HSTEP;

  //   if (!dnd.active) return;

  //   // Lane hit-test (use cached rects)
  //   const rects = colRectsRef.current;
  //   let hit = null;
  //   for (const k of Object.keys(rects)) {
  //     const r = rects[k];
  //     if (e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom) {
  //       hit = k; break;
  //     }
  //   }
  //   if (!hit) return;
  //   if (dnd.overCol !== hit) {
  //     setDnd(prev => ({ ...prev, overCol: hit, overIndex: (columns[hit] || []).length }));
  //   }

  //   // Vertical autoscroll for the hovered list + compute insertion index
  //   const listEl = listRefs[hit]?.current;
  //   if (!listEl) return;
  //   const lrect = listEl.getBoundingClientRect();
  //   const VTH = 56, VSTEP = 18;
  //   if (e.clientY < lrect.top + VTH) listEl.scrollTop -= VSTEP;
  //   else if (e.clientY > lrect.bottom - VTH) listEl.scrollTop += VSTEP;

  //   const draggingId = dragRef.current?.cardId || null;
  //   const prevIdx = lastOverRef.current?.idx ?? -1;

  //   // if (!dragCalcRaf.current) {
  //   //   pendingPointer.current = { key: hit, y: e.clientY };
  //   //   dragCalcRaf.current = requestAnimationFrame(() => {
  //   //     const { key, y } = pendingPointer.current;
  //   //     const el = listRefs[key]?.current;
  //   //     if (el) {
  //   //       const idx = computeIndexFromPointer(el, y, draggingId, prevIdx);
  //   //       setOver(key, idx);
  //   //     }
  //   //     dragCalcRaf.current = 0;
  //   //   });
  //   // } else {
  //   //   pendingPointer.current = { key: hit, y: e.clientY };
  //   // }
  // }

  // KEEP only this simplified version:
  function handleBoardDragOver(e) {
    e.preventDefault();
    if (!boardRef.current || !dnd.active) return;

    // Only handle horizontal scrolling here
    const boardEl = boardRef.current;
    const brect = boardEl.getBoundingClientRect();
    const HTH = 80,
      HSTEP = 24;
    if (e.clientX < brect.left + HTH) boardEl.scrollLeft -= HSTEP;
    else if (e.clientX > brect.right - HTH) boardEl.scrollLeft += HSTEP;

    // keep the floating preview under the cursor
    setDragPreviewPos(e.clientX, e.clientY);
  }

  function measureColumns() {
    const out = {};
    for (const { key } of STATUSES) {
      const el = sectionRefs[key]?.current;
      if (!el) continue;
      const r = el.getBoundingClientRect();
      out[key] = { left: r.left, right: r.right, top: r.top, bottom: r.bottom };
    }
    colRectsRef.current = out;
  }
  useEffect(() => {
    measureColumns();
    const onResize = () => measureColumns();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Debounced rank queue
  const RANK_DEBOUNCE_MS = 250;
  const rankQueueRef = useRef({ timer: null, pending: new Map() });

  useEffect(() => {
    loadPlanning(1, true); /* eslint-disable-next-line */
  }, []);
  useEffect(() => () => flushRankPatches(), []);

  // Capacity map
  const capacityMap = useMemo(
    () => buildCapacityMap(columns.Scheduled || []),
    [columns]
  );

  // Load planning
  async function loadPlanning(page, replace = false) {
    if (planningLoading || (!replace && !planningHasMore)) return;
    setPlanningLoading(true);
    setError("");
    try {
      const r = await fetch(`http://localhost:3001/production/orders?page=${page}&pageSize=15`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const js = await r.json();
      const cards = buildPlanningCards(js?.data || []);
      setColumns((prev) => {
        const next = replace ? emptyColumns() : clone(prev);
        const seen = new Set((next.Planned || []).map((c) => c.id));
        for (const c of cards)
          if (!seen.has(c.id))
            next.Planned.unshift(assignRankTop(next.Planned, c));
        return next;
      });
      const total = Number(js?.total ?? 0),
        size = Number(js?.pageSize ?? 15),
        cur = Number(js?.page ?? page);
      setPlanningHasMore(cur * size < total);
      setPlanningPage(cur);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setPlanningLoading(false);
    }
  }

  // Gentle overdue nudge
  useEffect(() => {
    setColumns((prev) => {
      const next = clone(prev);
      const list = next.Scheduled || [];
      let changed = false;
      const maxRank = list.reduce((m, c) => Math.max(m, c.rank || 0), 0);
      for (const c of list)
        if (
          !c.startedAt &&
          daysLate(c.plannedStart) > 0 &&
          (c.rank || 0) <= maxRank
        ) {
          c.rank = maxRank + 50;
          changed = true;
        }
      return changed ? next : prev;
    });
  }, []);

  // Helpers
  function emptyColumns() {
    const o = {};
    STATUSES.forEach((s) => (o[s.key] = []));
    return o;
  }
  const clone = (x) => JSON.parse(JSON.stringify(x));
  function buildPlanningCards(orders) {
    const out = [];
    for (const o of orders) {
      for (const it of o.items || []) {
        const decos = (it.decorations || []).filter(Boolean);
        if (!decos.length) continue;
        for (const d0 of decos) {
          const dept = inferDepartmentForDeco(d0);
          out.push({
            id: `ORD-${o.orderNumber}-${it.itemId}-${d0.decorationId}`,
            status: "Planned",
            orderNumber: o.orderNumber,
            customer: o.customerName || "",
            productName: it.productName || "",
            sku: it.productId || "",
            matrix: d0.chargeName || "",
            location: d0.location || "",
            qtyPlanned: Number(it.quantity || 0) || 0,
            qtyDone: 0,
            dueDate: o.inHandsDate || "",
            department: dept,
            claimedDept: "",
            lifecycle: [],
            rank: 0,
            __isStep: false,
            raw: {
              order: {
                orderNumber: o.orderNumber,
                customerName: o.customerName,
                inHandsDate: o.inHandsDate,
                projectName: o.projectName,
                poNumbers: o.poNumbers || [],
              },
              item: {
                itemId: it.itemId,
                productId: it.productId,
                productName: it.productName,
                quantity: it.quantity,
                sizes: it.sizes || [],
                colors: it.colors || [],
                netCostGoods: it.netCostGoods,
                retailCostGoods: it.retailCostGoods,
                supplierName: it.supplierName,
                category: it.category,
              },
              deco: {
                decorationId: d0.decorationId,
                chargeId: d0.chargeId,
                chargeName: d0.chargeName,
                location: d0.location,
                netCostGoods: d0.netCostGoods,
                retailCostGoods: d0.retailCostGoods,
                artProofUrl: d0.artProofUrl,
              },
            },
          });
        }
      }
    }
    return out;
  }
  function assignRankTop(arr, card) {
    const maxRank = arr.reduce((m, c) => Math.max(m, c.rank || 0), 0);
    return { ...card, rank: maxRank + 1000 };
  }
  function computeRankForIndex(arr, index) {
    const above = arr[index - 1],
      below = arr[index + 1];
    if (!above && !below) return 1000;
    if (!above) return (below?.rank || 0) + 1000;
    if (!below) return (above?.rank || 0) - 1000;
    return Math.round(((above.rank || 0) + (below.rank || 0)) / 2);
  }
  function estimateMinutes(card) {
    const ps = Array.isArray(card.processes) ? card.processes : [];
    const sum = ps
      .filter((p) => String(p.type || "").toLowerCase() === "machine")
      .reduce((t, p) => t + (Number(p.estMinutes) || 0), 0);
    return sum > 0 ? sum : DEFAULT_CARD_MIN;
  }
  function buildCapacityMap(list) {
    const map = {};
    for (const c of list) {
      if (!c.machine || !c.plannedStart) continue;
      const key = `${c.machine}::${c.plannedStart}`;
      const minutes = estimateMinutes(c);
      if (!map[key]) map[key] = { used: 0, cap: DEFAULT_DAY_CAPACITY_MIN };
      map[key].used += minutes;
    }
    return map;
  }
  function patchStatus(id, body) {
    return fetch(`/production/steps/${encodeURIComponent(id)}/status`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }).then((r) => {
      if (!r.ok) throw new Error(`Status save failed (${r.status})`);
    });
  }
  function queueRankPatch(id, rank) {
    const q = rankQueueRef.current;
    q.pending.set(id, rank);
    if (q.timer) clearTimeout(q.timer);
    q.timer = setTimeout(flushRankPatches, RANK_DEBOUNCE_MS);
  }
  function flushRankPatches() {
    const q = rankQueueRef.current;
    if (!q.pending.size) {
      q.timer && clearTimeout(q.timer);
      q.timer = null;
      return;
    }
    const payload = Array.from(q.pending.entries());
    q.pending.clear();
    q.timer = null;
    Promise.all(
      payload.map(([id, rank]) =>
        fetch(`/production/steps/${encodeURIComponent(id)}/rank`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ rank }),
        }).catch(() => {})
      )
    );
  }

  // Batch helpers
  function siblingsAcrossAllLanes(card, cols) {
    const groupId = card.rootId || card.id;
    const all = Object.keys(cols).flatMap((k) => cols[k]);
    return all.filter((c) => (c.rootId || c.id) === groupId);
  }
  function siblingsSameLane(card, cols) {
    const groupId = card.rootId || card.id;
    const arr = cols[card.status] || [];
    return arr.filter((c) => (c.rootId || c.id) === groupId);
  }
  function joinBatches(targetCard, otherIds) {
    setColumns((prev) => {
      const next = clone(prev);
      const lane = targetCard.status;
      const arr = next[lane] || [];
      const targetIdx = arr.findIndex((x) => x.id === targetCard.id);
      if (targetIdx === -1) return prev;

      // Collect children (target + selected)
      const groupId = targetCard.rootId || targetCard.id;
      const group = arr.filter((x) => (x.rootId || x.id) === groupId);
      const chosen = group.filter(
        (x) => x.id === targetCard.id || otherIds.includes(x.id)
      );

      const qtySum = chosen.reduce(
        (t, c) => t + (Number(c.qtyPlanned || 0) || 0),
        0
      );
      const doneSum = chosen.reduce(
        (t, c) => t + (Number(c.qtyDone || 0) || 0),
        0
      );
      const earliestDate = chosen.reduce(
        (min, c) =>
          c.plannedStart && (!min || c.plannedStart < min)
            ? c.plannedStart
            : min,
        null
      );
      const machineSet = new Set(chosen.map((c) => c.machine || ""));
      const machineMix =
        machineSet.size <= 1 ? chosen[0]?.machine || "" : "Multi";

      const merged = {
        ...targetCard,
        qtyPlanned: qtySum,
        qtyDone: doneSum,
        plannedStart: earliestDate || targetCard.plannedStart,
        machine: machineMix,
        lifecycle: [
          ...(targetCard.lifecycle || []),
          {
            when: new Date().toLocaleString(),
            text: `Merged ${chosen.length - 1} batch(es) into this card`,
          },
        ],
      };

      const keep = arr.filter(
        (x) => !otherIds.includes(x.id) && x.id !== targetCard.id
      );
      keep.splice(targetIdx, 0, merged);
      next[lane] = keep;
      return next;
    });
  }

  // compute if move is allowed (matches your rules)
  function canMoveBetween(fromKey, toKey, lastMain) {
    if (!fromKey || !toKey) return false;
    if (fromKey === "Planned" && toKey === "Paused") return false; // block Planning -> Paused
    if (fromKey === "Paused") return toKey === (lastMain || "Planned"); // return only to last main lane
    if (toKey === "Paused") return true; // allow Paused from anywhere (except Planning above)
    const fi = STATUS_INDEX[fromKey],
      ti = STATUS_INDEX[toKey];
    return Math.abs(ti - fi) === 1; // adjacency
  }
  function computeAllowedTargets(fromKey, lastMain) {
    const s = new Set();
    for (const { key } of STATUSES)
      if (canMoveBetween(fromKey, key, lastMain)) s.add(key);
    return s;
  }

  // Auto-scroll the list you’re hovering over while dragging
  function handleDragOverList(colKey, e) {
    e.preventDefault();
    const el = listRefs[colKey]?.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    const y = e.clientY;
    const threshold = 56;
    const maxStep = 18;

    if (y < rect.top + threshold) {
      const delta = Math.min(
        maxStep,
        Math.ceil((rect.top + threshold - y) / 2)
      );
      el.scrollTop -= delta;
    } else if (y > rect.bottom - threshold) {
      const delta = Math.min(
        maxStep,
        Math.ceil((y - (rect.bottom - threshold)) / 2)
      );
      el.scrollTop += delta;
    }
  }

  // outline style per column
  function getColOutlineStyle(colKey) {
    if (!dnd.active || dnd.overCol !== colKey) return null;
    if (dnd.from === dnd.overCol) return ui.colSameHighlight;
    return dnd.allowed.has(colKey) ? ui.colHighlight : ui.colBlockedHighlight;
  }

  // DnD
  function onDragStart(e, card, fromCol, fromIndex) {
    measureColumns();
    // Single, stable transparent drag image to prevent browser re-snapshot flicker
    try {
      const img = new Image();
      img.src = "data:image/gif;base64,R0lGODlhAQABAAAAACw=";
      e.dataTransfer.setDragImage(img, 0, 0);
    } catch {}

    const isMain = fromCol !== "Paused";
    const lastMain = isMain ? fromCol : card._lastMain || "Planned";

    // Measure height incl. margins (for end-of-list ghost height)
    const rect = e.currentTarget.getBoundingClientRect();
    const cs = window.getComputedStyle(e.currentTarget);
    const marginBlock =
      parseFloat(cs.marginTop || 0) + parseFloat(cs.marginBottom || 0);
    dragRef.current.cardHeight = rect.height + marginBlock;

    dragRef.current = {
      cardId: card.id,
      from: fromCol,
      fromIndex,
      overIndex: fromIndex,
      overCol: fromCol,
      lastMain,
      cardHeight: dragRef.current.cardHeight,
    };

    // Build a floating clone for Trello-like preview
    try {
      const clone = e.currentTarget.cloneNode(true);
      const rect = e.currentTarget.getBoundingClientRect();
      Object.assign(clone.style, {
        position: "fixed",
        left: "0px",
        top: "0px",
        width: `${rect.width}px`,
        height: `${rect.height}px`,
        pointerEvents: "none",
        zIndex: 99999,
        transform: `translate(${e.clientX + 12}px, ${e.clientY + 12}px)`,
        opacity: 1,
        // feel free to tweak visuals; this reuses your dashed “drop preview” look:
        ...ui.dropPreviewCard,
      });
      document.body.appendChild(clone);
      dragPreviewRef.current.el = clone;
    } catch {}

    const allowed = computeAllowedTargets(fromCol, lastMain);
    setDnd({
      active: true,
      from: fromCol,
      lastMain,
      allowed,
      overCol: fromCol,
      overIndex: fromIndex,
    });
    e.dataTransfer.effectAllowed = "move";
    try {
      e.dataTransfer.setData("text/plain", card.id);
    } catch {}
  }

  // function onDragEnd(){
  //   setDnd({ active:false, from:null, lastMain:null, allowed:new Set(), overCol:null, overIndex:-1 });
  //   lastOverRef.current = { col:null, idx:-1 };
  //   dragRef.current = { cardId:null, from:null, fromIndex:-1, overIndex:-1, overCol:null, lastMain:null, cardHeight:0 };
  // }

  function onDragEnd() {
    setDnd({
      active: false,
      from: null,
      lastMain: null,
      allowed: new Set(),
      overCol: null,
      overIndex: -1,
    });
    lastOverRef.current = { col: null, idx: -1 };
    dragRef.current = {
      cardId: null,
      from: null,
      fromIndex: -1,
      overIndex: -1,
      overCol: null,
      lastMain: null,
      cardHeight: 0,
    };

    // stop any queued compute frame from firing after drop
    if (dragCalcRaf.current) {
      cancelAnimationFrame(dragCalcRaf.current);
      dragCalcRaf.current = 0;
    }
    // remove the floating preview
    const p = dragPreviewRef.current;
    if (p.el && p.el.parentNode) p.el.parentNode.removeChild(p.el);
    dragPreviewRef.current.el = null;
  }

  const lastOverRef = useRef({ col: null, idx: -1 });
  const setOver = (col, idx) => {
    const { col: lc, idx: li } = lastOverRef.current;
    if (lc !== col || li !== idx) {
      lastOverRef.current = { col, idx };
      dragRef.current.overCol = col;
      dragRef.current.overIndex = idx;
      setDnd((prev) => ({ ...prev, overCol: col, overIndex: idx }));
    }
  };

  function handleListDragEnter(colKey) {
    const len = (columns[colKey] || []).length;
    setOver(colKey, len);
  }

  // ---- Unified index helpers (visual vs actual) ----
  function getVisualOverIndex(colKey) {
    if (!dnd.active || dnd.overCol !== colKey) return -1;
    return dnd.overIndex ?? -1;
  }

  // Final index to use when actually inserting on drop
  function getFinalDropIndex(toCol) {
    if (!dnd.active) return -1;

    // If the pointer is over the column (header/empty zone), fallback to end
    const overList = dnd.overCol === toCol;
    if (!overList) return (columns[toCol] || []).length;

    const oi = dnd.overIndex;
    if (oi < 0) return -1;

    // Same-lane, moving down → shift back by one
    if (dragRef.current.from === toCol && dragRef.current.fromIndex < oi)
      return Math.max(0, oi - 1);
    return oi;
  }

  // Drop
  async function onDrop(e, toCol) {
    e.preventDefault();
    const { cardId, from, overIndex, overCol, lastMain } =
      dragRef.current || {};
    if (!cardId || !from) return;

    // Same-lane reorder (pure, no awaits)
    if (
      from === toCol &&
      overCol === toCol &&
      typeof overIndex === "number" &&
      overIndex >= 0
    ) {
      setColumns((prev) => {
        const next = clone(prev);
        const arr = next[toCol];
        const i = arr.findIndex((c) => c.id === cardId);
        if (i === -1) return prev;

        const targetIndex = getFinalDropIndex(toCol);

        const [moved] = arr.splice(i, 1);
        arr.splice(targetIndex, 0, moved);

        const newRank = computeRankForIndex(arr, targetIndex);
        moved.rank = newRank;
        if (toCol !== "Planned" && moved.__isStep)
          queueRankPatch(moved.id, newRank);
        return next;
      });
      return;
    }

    // Disallow Planning → Paused (QC → Paused is allowed)
    if (from === "Planned" && toCol === "Paused") {
      await sweet.alert({
        type: "warn",
        title: "Move blocked",
        content:
          "You cannot move directly from Planning to Paused. Please schedule or progress the job first.",
      });
      return;
    }

    // Adjacency across main lanes (Paused is special)
    if (toCol !== "Paused" && from !== "Paused") {
      const fi = STATUS_INDEX[from],
        ti = STATUS_INDEX[toCol];
      const delta = ti - fi;
      if (Math.abs(delta) !== 1) {
        await sweet.alert({
          type: "warn",
          title: "Move blocked",
          content: "Only adjacent moves are allowed (except Paused/Hold).",
        });
        return;
      }
      // Backward → confirmation
      if (delta < 0) {
        const goingToPlanned = toCol === "Planned";
        const bullets = goingToPlanned
          ? [
              "Department claim will be cleared.",
              "Schedule fields are removed from the card view.",
              "Card will return to Planning and appear at the top.",
            ]
          : [
              `Card will move back to ${toCol.replace(/_/g, " ")} for rework.`,
              "Rank may change (card goes to the top).",
              "Capacity bars ignore until re-scheduled.",
            ];
        const ok = await sweet.confirm({
          type: "warn",
          title: `Move back to ${toCol.replace(/_/g, " ")}?`,
          content: (
            <div>
              <p>
                You’re moving this job backward from{" "}
                <b>{from.replace(/_/g, " ")}</b> to{" "}
                <b>{toCol.replace(/_/g, " ")}</b>. Consequences:
              </p>
              <ul>
                {bullets.map((b, i) => (
                  <li key={i}>{b}</li>
                ))}
              </ul>
            </div>
          ),
          confirmText: "Yes, move back",
          cancelText: "No, keep here",
        });
        if (!ok) return;
      }
    }

    // Look up the card once
    const fromArr0 = columns[from] || [];
    const idx0 = fromArr0.findIndex((c) => c.id === cardId);
    if (idx0 === -1) return;
    const moved0 = fromArr0[idx0];

    // Paused return rule: only back to its last main lane
    if (from === "Paused" && toCol !== (moved0._lastMain || "Planned")) {
      await sweet.alert({
        type: "warn",
        title: "Not allowed",
        content: `Paused card can only return to ${
          moved0._lastMain || "Planned"
        }.`,
      });
      return;
    }

    // Move Gate to Scheduled (open modal; no state change yet)
    if (toCol === "Scheduled") {
      setMoveModal({
        open: true,
        to: "Scheduled",
        card: moved0,
        fields: {
          operator: "",
          machine: "",
          plannedStart: "",
          plannedFinish: "",
          reason: "",
        },
      });
      return;
    }

    // Starting late → soft prompt
    if (
      toCol === "In_Production" &&
      moved0.plannedStart &&
      daysLate(moved0.plannedStart) > 0
    ) {
      const ok = await sweet.confirm({
        type: "warn",
        title: "Start past its scheduled date?",
        content: (
          <div>
            <p>
              This job is scheduled for <b>{DATE_FMT(moved0.plannedStart)}</b>.
            </p>
            <p>Start now anyway?</p>
          </div>
        ),
        confirmText: "Start now",
        cancelText: "Reschedule to today",
      });

      if (!ok) {
        const newDay = todayYMD();
        setColumns((prev) => {
          const next = clone(prev);
          const arr = next[from] || [];
          const i = arr.findIndex((c) => c.id === cardId);
          if (i !== -1) {
            arr[i].plannedStart = newDay;
            arr[i].plannedFinish = newDay;
            arr[i].lifecycle = [
              ...(arr[i].lifecycle || []),
              {
                when: new Date().toLocaleString(),
                text: "Rescheduled to today",
              },
            ];
          }
          return next;
        });
        if (moved0.__isStep) {
          try {
            await patchStatus(moved0.id, {
              fromStatus: "Scheduled",
              toStatus: "Scheduled",
              plannedStart: toISOStart(newDay),
              plannedFinish: toISOEnd(newDay),
              reason: "Rescheduled on start",
            });
          } catch {}
        }
        return;
      }
    }

    // Perform the move
    setColumns((prev) => {
      const next = clone(prev);
      const fromArr = next[from] || [];
      const i = fromArr.findIndex((c) => c.id === cardId);
      if (i === -1) return prev;
      const [moved] = fromArr.splice(i, 1);

      // Paused bookkeeping
      if (toCol === "Paused") {
        moved._lastMain = from === "Paused" ? lastMain : from;
        moved.lifecycle = moved.lifecycle || [];
        moved.lifecycle.push({
          when: new Date().toLocaleString(),
          text: `Moved to Paused/Hold from ${from}`,
        });
      }

      // Clear claim/schedule if going back to Planning
      if (toCol === "Planned") {
        moved.claimedDept = "";
        moved.operator = "";
        moved.machine = "";
        moved.plannedStart = "";
        moved.plannedFinish = "";
        moved.lifecycle = moved.lifecycle || [];
        moved.lifecycle.push({
          when: new Date().toLocaleString(),
          text: "Returned to Planning (claim cleared)",
        });
      }

      moved.status = toCol;
      // next[toCol].unshift(assignRankTop(next[toCol], moved));

      const arrTo = next[toCol];
      const dropAt = getFinalDropIndex(toCol);
      arrTo.splice(
        Math.min(dropAt, arrTo.length),
        0,
        assignRankTop(arrTo, moved)
      );
      return next;
    });

    // Persist (non-blocking)
    if (moved0.__isStep && toCol !== "Planned") {
      patchStatus(moved0.id, { fromStatus: from, toStatus: toCol }).catch(
        () => {}
      );
    }
    setDnd({
      active: false,
      from: null,
      lastMain: null,
      allowed: new Set(),
      overCol: null,
      overIndex: -1,
    });
  }

  // Planning infinite scroll
  function onScrollPlanning(e) {
    const el = e.currentTarget;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 100;
    if (nearBottom && planningHasMore && !planningLoading)
      loadPlanning(planningPage + 1);
  }
  function onScrollCol(key, e) {
    if (key === "Planned") onScrollPlanning(e);
  }

  // Modal helpers
  function openCard(card) {
    setModalCard(card);
    setModalOpen(true);
  }
  function onPatchCard(id, patch) {
    setColumns((prev) => {
      const next = clone(prev);
      const all = Object.keys(next).flatMap((k) => next[k]);
      const c = all.find((x) => x.id === id);
      if (c) Object.assign(c, patch);
      if (modalCard?.id === id) setModalCard({ ...modalCard, ...patch });
      return next;
    });
  }
  function onCancelCard(card, reason) {
    const snap = JSON.stringify(columns);
    setColumns((prev) => {
      const next = clone(prev);
      for (const k of Object.keys(next)) {
        const arr = next[k];
        const i = arr.findIndex((c) => c.id === card.id);
        if (i !== -1) {
          arr[i].cancelled = true;
          arr[i].lifecycle = [
            ...(arr[i].lifecycle || []),
            {
              when: new Date().toLocaleString(),
              text: `Cancelled: ${reason || "—"}`,
            },
          ];
          break;
        }
      }
      return next;
    });
    if (card.__isStep) {
      patchStatus(card.id, {
        fromStatus: card.status,
        toStatus: "Cancelled",
        reason,
      }).catch(() => setColumns(JSON.parse(snap)));
    }
  }
  function onReinstateCard(card, note) {
    const snap = JSON.stringify(columns);
    setColumns((prev) => {
      const next = clone(prev);
      for (const k of Object.keys(next)) {
        const arr = next[k];
        const i = arr.findIndex((c) => c.id === card.id);
        if (i !== -1) {
          arr[i].cancelled = false;
          arr[i].lifecycle = [
            ...(arr[i].lifecycle || []),
            {
              when: new Date().toLocaleString(),
              text: `Reinstated: ${note || "—"}`,
            },
          ];
          break;
        }
      }
      return next;
    });
    if (card.__isStep) {
      patchStatus(card.id, {
        fromStatus: "Cancelled",
        toStatus: "Scheduled",
        reason: note,
      }).catch(() => setColumns(JSON.parse(snap)));
    }
  }

  // Confirm schedule
  async function confirmSchedule(fields) {
    const yStart = fields.plannedStart,
      yFinish = fields.plannedFinish;
    const isoStart = toISOStart(yStart),
      isoFinish = toISOEnd(yFinish);

    const snapshot = JSON.stringify(columns);
    const next = JSON.parse(snapshot);

    const fromKey = Object.keys(next).find((k) =>
      (next[k] || []).some((c) => c.id === moveModal.card.id)
    );
    if (!fromKey)
      return setMoveModal({ open: false, to: null, card: null, fields: {} });
    const arr = next[fromKey];
    const idx = arr.findIndex((c) => c.id === moveModal.card.id);
    if (idx === -1)
      return setMoveModal({ open: false, to: null, card: null, fields: {} });
    const orig = arr[idx];
    arr.splice(idx, 1);

    const scheduled = {
      ...orig,
      status: "Scheduled",
      claimedDept:
        orig.department ||
        inferDepartmentForDeco(orig.raw?.deco) ||
        "Department",
      plannedStart: yStart,
      plannedFinish: yFinish,
      operator: fields.operator,
      machine: fields.machine,
      reason: fields.reason || "",
      lifecycle: [
        ...(orig.lifecycle || []),
        {
          when: new Date().toLocaleString(),
          text: `Scheduled (${DATE_FMT(yStart)}) • ${
            fields.operator || "Unassigned"
          } on ${fields.machine || "TBD"}`,
        },
      ],
      __isStep: true,
    };
    next.Scheduled.unshift(assignRankTop(next.Scheduled, scheduled));
    setColumns(next);
    setMoveModal({ open: false, to: null, card: null, fields: {} });

    try {
      await patchStatus(scheduled.id, {
        fromStatus: fromKey,
        toStatus: "Scheduled",
        operator: fields.operator,
        machine: fields.machine,
        plannedStart: isoStart,
        plannedFinish: isoFinish,
        qty: scheduled.qtyPlanned,
        reason: fields.reason || "",
      });
      queueRankPatch(scheduled.id, scheduled.rank);
    } catch (e) {
      await sweet.alert({
        type: "error",
        title: "Could not save schedule",
        content: String(e.message || e),
      });
      setColumns(JSON.parse(snapshot));
    }
  }

  // Reorder ↑/↓
  function reorder(dir, colKey, cardId) {
    setColumns((prev) => {
      const next = clone(prev);
      const arr = next[colKey] || [];
      const i = arr.findIndex((c) => c.id === cardId);
      if (i === -1) return prev;
      const j = dir === "up" ? i - 1 : i + 1;
      if (j < 0 || j >= arr.length) return prev;
      [arr[i], arr[j]] = [arr[j], arr[i]];
      const newRank = computeRankForIndex(arr, j);
      arr[j].rank = newRank;
      if (colKey !== "Planned" && arr[j].__isStep)
        queueRankPatch(arr[j].id, newRank);
      return next;
    });
  }

  return (
    <div ref={boardRef} style={ui.wrap} onDragOver={handleBoardDragOver}>
      <header style={ui.toolbar}>
        <div
          style={{
            display: "flex",
            gap: 12,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <strong style={{ fontSize: 18 }}>Production Board</strong>
          {planningLoading && <span style={ui.pill}>Loading…</span>}
          {error && <span style={ui.errPill}>Error: {error}</span>}
        </div>
      </header>
      <div style={ui.board}>
        {STATUSES.map(({ key, label }) => (
          <section
            key={key}
            ref={sectionRefs[key]}
            style={{ ...ui.column, ...(getColOutlineStyle(key) || {}) }}
            onDragOver={(e) => {
              e.preventDefault();
            }}
            onDrop={(e) => onDrop(e, dnd.overCol || key)}
          >
            <div style={ui.colHeader}>
              <h3 style={{ margin: 0, fontSize: 18 }}>{label}</h3>
            </div>

            <div
              ref={listRefs[key]}
              style={ui.cardList}
              onScroll={(e) => onScrollCol(key, e)}
              onDragEnter={() => handleListDragEnter(key)}
              onDragOver={(e) => {
                if (!dnd.active) return;

                e.preventDefault();

                setDragPreviewPos(e.clientX, e.clientY);

                handleDragOverList(key, e);
                pendingPointer.current = { key, y: e.clientY };
                if (!dragCalcRaf.current) {
                  dragCalcRaf.current = requestAnimationFrame(() => {
                    const { key: col, y } = pendingPointer.current;
                    const el = listRefs[col]?.current;
                    if (el) {
                      const draggingId = dragRef.current?.cardId || null;
                      const prevIdx = lastOverRef.current?.idx ?? -1;
                      const idx = computeIndexFromPointer(
                        el,
                        y,
                        draggingId,
                        prevIdx
                      );
                      if (idx !== prevIdx || lastOverRef.current.col !== col)
                        setOver(col, idx);
                    }
                    dragCalcRaf.current = 0;
                  });
                }
              }}
            >
              {(columns[key] || []).map((c, idx) => {
                const visualIndex = getVisualOverIndex(key);
                const capKey =
                  c.machine && c.plannedStart
                    ? `${c.machine}::${c.plannedStart}`
                    : null;
                const cap = capKey ? capacityMap[capKey] : null;
                const percent = cap
                  ? Math.round((cap.used / Math.max(1, cap.cap)) * 100)
                  : null;
                const over = percent != null && percent > 100;
                const isDraggingThis =
                  dnd.active && dragRef.current.cardId === c.id;
                const ghostH = dragRef.current.cardHeight || 60;
                const sibsAll = siblingsAcrossAllLanes(c, columns);
                const showBatchBadge = sibsAll.length > 1;

                return (
                  <React.Fragment key={c.id}>
                    {dnd.active &&
                      dnd.overCol === key &&
                      visualIndex === idx && <div style={ui.insertMarker} />}
                    <article
                      data-cardid={c.id}
                      data-index={idx}
                      style={{
                        ...ui.card,
                        ...(c.cancelled ? ui.cardCancelled : {}),
                        ...(isDraggingThis ? ui.dragOriginShadow : {}),
                        // ...(isDraggingThis ? ui.dragOriginCollapse : {}),
                        // ...(isDraggingThis ? ui.dragOriginHidden : {}),
                      }}
                      draggable={!c.cancelled}
                      onDragStart={(e) => onDragStart(e, c, key, idx)}
                      onDragEnd={onDragEnd}
                    >
                      <div style={ui.cardTop}>
                        <div style={{ minWidth: 0 }}>
                          <div
                            style={{
                              fontWeight: 800,
                              fontSize: 16,
                              lineHeight: 1.25,
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                              flexWrap: "wrap",
                            }}
                          >
                            <span
                              style={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {c.orderNumber || c.title || "—"}
                            </span>
                            {key === "Scheduled" &&
                              !c.startedAt &&
                              c.plannedStart &&
                              daysLate(c.plannedStart) > 0 && (
                                <span style={ui.overduePill}>
                                  Overdue {daysLate(c.plannedStart)}d
                                </span>
                              )}
                            {showBatchBadge && (
                              <span style={ui.badgeSmall}>Batch</span>
                            )}
                          </div>
                          <div
                            style={{
                              fontSize: 13.5,
                              color: "#555",
                              marginTop: 2,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {c.customer || ""}
                          </div>
                          <div
                            style={{
                              fontSize: 13.5,
                              color: "#667",
                              marginTop: 2,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {c.productName || ""}{" "}
                            <span style={{ color: "#999" }}>
                              ({c.sku || ""})
                            </span>
                          </div>
                        </div>
                        <span style={ui.badge} title={`Rank ${c.rank}`}>
                          {c.cancelled
                            ? "Cancelled"
                            : c.claimedDept
                            ? `${c.claimedDept} • Planned`
                            : c.department || key.replace(/_/g, " ")}
                        </span>
                      </div>

                      <div style={ui.cardGrid}>
                        <div>
                          <div style={ui.subtle}>Qty</div>
                          <div style={ui.valStrong}>{c.qtyPlanned ?? 0}</div>
                        </div>
                        <div>
                          <div style={ui.subtle}>Location</div>
                          <div style={ui.valStrong}>{c.location || "–"}</div>
                        </div>
                        <div>
                          <div style={ui.subtle}>Matrix</div>
                          <div style={ui.valStrong}>{c.matrix || "–"}</div>
                        </div>
                        {key !== "Planned" && (
                          <>
                            <div>
                              <div style={ui.subtle}>Date</div>
                              <div style={ui.valStrong}>
                                {c.plannedStart
                                  ? DATE_FMT(c.plannedStart)
                                  : "–"}
                              </div>
                            </div>
                            <div>
                              <div style={ui.subtle}>Machine</div>
                              <div style={ui.valStrong}>
                                {c.machine || (showBatchBadge ? "Multi" : "–")}
                              </div>
                            </div>
                          </>
                        )}
                      </div>

                      {key === "Scheduled" && cap && (
                        <div style={{ marginTop: 10 }}>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              fontSize: 13,
                              marginBottom: 4,
                            }}
                          >
                            <span>
                              Machine load • {c.machine} •{" "}
                              {DATE_FMT(c.plannedStart)}
                            </span>
                            <span>
                              {cap.used}/{cap.cap} min{over ? " • High" : ""}
                            </span>
                          </div>
                          <div style={capStyles.track}>
                            <div
                              style={{
                                ...capStyles.fill,
                                width: `${Math.min(100, percent)}%`,
                                background: over ? "#fca5a5" : "#86efac",
                              }}
                            />
                          </div>
                        </div>
                      )}

                      <div style={ui.cardActions}>
                        <button
                          style={ui.btnBlue}
                          onClick={() => {
                            setModalCard(c);
                            setModalOpen(true);
                          }}
                        >
                          Open
                        </button>

                        {key === "Scheduled" && !c.cancelled && (
                          <>
                            <button
                              style={ui.btnGreen}
                              onClick={async () => {
                                const snap = JSON.stringify(columns);
                                setColumns((prev) => {
                                  const next = JSON.parse(snap);
                                  const i = next.Scheduled.findIndex(
                                    (x) => x.id === c.id
                                  );
                                  if (i === -1) return prev;
                                  const [card] = next.Scheduled.splice(i, 1);
                                  card.status = "In_Production";
                                  card.startedAt = todayYMD();
                                  card.lifecycle = [
                                    ...(card.lifecycle || []),
                                    {
                                      when: new Date().toLocaleString(),
                                      text: "Started",
                                    },
                                  ];

                                  next.In_Production.unshift(
                                    assignRankTop(next.In_Production, card)
                                  );
                                  return next;
                                });
                                if (c.__isStep) {
                                  try {
                                    await patchStatus(c.id, {
                                      fromStatus: "Scheduled",
                                      toStatus: "In_Production",
                                    });
                                  } catch {
                                    setColumns(JSON.parse(snap));
                                    await sweet.alert({
                                      type: "error",
                                      title: "Failed to start",
                                      content: "Could not save status.",
                                    });
                                  }
                                }
                              }}
                            >
                              Start now
                            </button>

                            <button
                              style={ui.btnAmber}
                              onClick={async () => {
                                const reason = await sweet.prompt({
                                  type: "warn",
                                  title: "Reschedule to today",
                                  content: "(Optional) provide a short note.",
                                  placeholder: "Note (optional)",
                                  required: false,
                                  confirmText: "Reschedule",
                                  cancelText: "Cancel",
                                });
                                const snap = JSON.stringify(columns);
                                setColumns((prev) => {
                                  const next = JSON.parse(snap);
                                  const i = next.Scheduled.findIndex(
                                    (x) => x.id === c.id
                                  );
                                  if (i === -1) return prev;
                                  next.Scheduled[i].plannedStart = todayYMD();
                                  next.Scheduled[i].plannedFinish = todayYMD();
                                  next.Scheduled[i].lifecycle = [
                                    ...(next.Scheduled[i].lifecycle || []),
                                    {
                                      when: new Date().toLocaleString(),
                                      text: `Rescheduled to today${
                                        reason ? ` • ${reason}` : ""
                                      }`,
                                    },
                                  ];
                                  return next;
                                });
                                if (c.__isStep) {
                                  try {
                                    await patchStatus(c.id, {
                                      fromStatus: "Scheduled",
                                      toStatus: "Scheduled",
                                      plannedStart: toISOStart(todayYMD()),
                                      plannedFinish: toISOEnd(todayYMD()),
                                      reason: reason || "",
                                    });
                                  } catch {
                                    setColumns(JSON.parse(snap));
                                    await sweet.alert({
                                      type: "error",
                                      title: "Failed to reschedule",
                                      content: "Could not save the new date.",
                                    });
                                  }
                                }
                              }}
                            >
                              Reschedule
                            </button>

                            <button
                              style={ui.btnIndigo}
                              onClick={() =>
                                setSplitUI({ open: true, card: c })
                              }
                            >
                              Split…
                            </button>
                          </>
                        )}

                        {key === "In_Production" && !c.cancelled && (
                          <button
                            style={ui.btnPurple}
                            onClick={() =>
                              setPartialMove({
                                open: true,
                                from: "In_Production",
                                card: c,
                                defaultTo: "QC",
                              })
                            }
                          >
                            Partial…
                          </button>
                        )}

                        <div
                          style={{
                            marginLeft: "auto",
                            display: "flex",
                            gap: 8,
                          }}
                        >
                          <button
                            title="Move up"
                            style={ui.iconBtn}
                            onClick={() => reorder("up", key, c.id)}
                          >
                            ↑
                          </button>
                          <button
                            title="Move down"
                            style={ui.iconBtn}
                            onClick={() => reorder("down", key, c.id)}
                          >
                            ↓
                          </button>
                        </div>
                      </div>
                    </article>
                  </React.Fragment>
                );
              })}
              {/* Ghost at END of list */}
              {dnd.active &&
                dnd.overCol === key &&
                getVisualOverIndex(key) === (columns[key] || []).length && (
                  <div
                    style={{
                      ...ui.dropGhost,
                      height: dragRef.current.cardHeight || 60,
                    }}
                  />
                )}
              {key === "Planned" &&
                !planningHasMore &&
                (columns.Planned || []).length > 0 && (
                  <div style={ui.endRow}>End of list</div>
                )}
            </div>
          </section>
        ))}
      </div>
      {/* Job Card */}
      {modalOpen && (
        <JobCardModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          card={modalCard}
          onPatchCard={onPatchCard}
          onCancelCard={onCancelCard}
          onReinstateCard={onReinstateCard}
          siblingsAll={
            modalCard ? siblingsAcrossAllLanes(modalCard, columns) : []
          }
          siblingsSameLane={
            modalCard ? siblingsSameLane(modalCard, columns) : []
          }
          onOpenSplit={(card) => setSplitUI({ open: true, card })}
          onOpenJoin={(card) => setJoinUI({ open: true, card })}
        />
      )}
      {/* Move Gate */}
      {moveModal.open && (
        <MoveGateModal
          to={moveModal.to}
          fields={moveModal.fields}
          onCancel={() => setMoveModal({ ...moveModal, open: false })}
          onConfirm={(form) => confirmSchedule(form)}
        />
      )}
      {/* Split wizard */}
      {splitUI.open && (
        <SplitWizard
          open={splitUI.open}
          card={splitUI.card}
          onCancel={() => setSplitUI({ open: false, card: null })}
          onConfirm={(rows) => {
            const valid = rows.filter((r) => clampInt(r.qty, 1, 1e9) > 0);
            const total = valid.reduce(
              (t, r) => t + clampInt(r.qty, 1, 1e9),
              0
            );
            const available = Math.max(
              0,
              Number(splitUI.card.qtyPlanned || 0) -
                Number(splitUI.card.qtyDone || 0)
            );
            if (!valid.length || total < 1 || total > available) return;

            setColumns((prev) => {
              const next = clone(prev);
              const lane = "Scheduled";
              const arr = next[lane];
              const i = arr.findIndex((x) => x.id === splitUI.card.id);
              if (i === -1) return prev;

              const parent = arr[i];
              const rootId = parent.rootId || parent.id;
              const remaining = available - total;
              arr[i] = {
                ...parent,
                rootId,
                qtyPlanned: remaining,
                lifecycle: [
                  ...(parent.lifecycle || []),
                  {
                    when: new Date().toLocaleString(),
                    text: `Split ${total} into ${valid.length} batch(es)`,
                  },
                ],
              };

              valid.forEach((r) => {
                const childId = `${rootId}#b${Math.floor(
                  Math.random() * 100000
                )}`;
                const child = {
                  ...parent,
                  id: childId,
                  rootId,
                  qtyPlanned: clampInt(r.qty, 1, 1e9),
                  qtyDone: 0,
                  machine: r.machine || "",
                  plannedStart: r.date || parent.plannedStart,
                  lifecycle: [
                    ...(parent.lifecycle || []),
                    {
                      when: new Date().toLocaleString(),
                      text: `Created batch ${clampInt(r.qty, 1, 1e9)} • ${
                        r.machine || "TBD"
                      } • ${DATE_FMT(r.date || parent.plannedStart)}`,
                    },
                  ],
                  __local: true,
                };
                next[lane].unshift(assignRankTop(next[lane], child));
              });
              return next;
            });

            setSplitUI({ open: false, card: null });
          }}
        />
      )}
      {/* Join wizard (same-lane) */}
      {joinUI.open && (
        <JoinWizard
          open={joinUI.open}
          card={joinUI.card}
          siblings={joinUI.card ? siblingsSameLane(joinUI.card, columns) : []}
          onCancel={() => setJoinUI({ open: false, card: null })}
          onConfirm={(ids) => {
            joinBatches(joinUI.card, ids);
            setJoinUI({ open: false, card: null });
          }}
        />
      )}
      {/* Partial move */}
      {partialMove.open && (
        <PartialMoveModal
          open={partialMove.open}
          from={partialMove.from}
          defaultTo={partialMove.defaultTo}
          card={partialMove.card}
          onCancel={() =>
            setPartialMove({
              open: false,
              from: null,
              card: null,
              defaultTo: "QC",
            })
          }
          onConfirm={async ({ qty, to }) => {
            const baseSnap = JSON.stringify(columns);
            const res = createBatchFrom(partialMove.card, qty, to);
            if (!res) {
              await sweet.alert({
                type: "warn",
                title: "Invalid quantity",
                content:
                  "Please enter a valid quantity within the available range.",
              });
              return;
            }
            const { child, parentAfter } = res;

            setColumns((prev) => {
              const next = JSON.parse(JSON.stringify(prev));
              const fromArr = next[partialMove.from];
              const idx = fromArr.findIndex(
                (x) => x.id === partialMove.card.id
              );
              if (idx === -1) return prev;
              fromArr[idx] = parentAfter;
              next[to].unshift(assignRankTop(next[to], child));
              return next;
            });

            if (child.__isStep) {
              try {
                await patchStatus(child.id, {
                  fromStatus: partialMove.from,
                  toStatus: to,
                  qty,
                });
              } catch {
                setColumns(JSON.parse(baseSnap));
                await sweet.alert({
                  type: "error",
                  title: "Split failed",
                  content: "Could not persist partial move.",
                });
              }
            }

            setPartialMove({
              open: false,
              from: null,
              card: null,
              defaultTo: "QC",
            });
          }}
        />
      )}
      {/* Sweet host */}
      {sweetNode}
    </div>
  );
}

//
// ────────────────────────────────────────────────────────────────────────────
// Styles (larger fonts, touch targets, non-overlapping grids)
// ────────────────────────────────────────────────────────────────────────────
const FONT = 16; // base font size
const TAP = 44; // min touch target size

const ui = {
  colHighlight: { outline: "3px dashed #22c55e", outlineOffset: "-3px" }, // hovered & allowed
  colSameHighlight: { outline: "3px solid #22c55e", outlineOffset: "-3px" },
  colBlockedHighlight: { outline: "3px dashed #ef4444", outlineOffset: "-3px" }, // hovered but blocked
  wrap: { padding: 16, overflowX: "auto", fontSize: FONT },
  toolbar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
  },
  board: { display: "flex", gap: 16, minWidth: "1800px" },
  column: {
    flex: "0 0 360px",
    background: "#f8f9fb",
    border: "1px solid #eef0f5",
    borderRadius: 12,
    display: "flex",
    flexDirection: "column",
    maxHeight: "calc(100vh - 160px)",
  },
  colHeader: {
    padding: 14,
    borderBottom: "1px solid #eef0f5",
    background: "#f1f5f9",
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
  },
  cardList: { padding: 12, overflowY: "auto", flex: 1 },

  card: {
    background: "#fff",
    border: "1px solid #ddd",
    borderRadius: 12,
    padding: 12,
    marginBottom: 12,
    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
    userSelect: "none",
    willChange: "transform",
  },
  cardTop: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    alignItems: "flex-start",
  },
  cardGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
    gap: 10,
    marginTop: 8,
    fontSize: 14,
  },

  cardActions: {
    marginTop: 10,
    display: "flex",
    gap: 8,
    alignItems: "center",
    flexWrap: "wrap",
    justifyContent: "flex-end",
  },
  valStrong: { fontWeight: 700 },
  linkBtn: {
    border: "1px solid #ccc",
    borderRadius: 8,
    padding: "10px 14px",
    fontSize: 15,
    cursor: "pointer",
    background: "#fff",
    minHeight: TAP,
  },

  iconBtn: {
    border: "1px solid #ccc",
    borderRadius: 8,
    padding: "6px 10px",
    fontSize: 15,
    cursor: "pointer",
    background: "#fff",
    minHeight: TAP,
  },

  btnGhost: {
    border: "1px solid #ccc",
    background: "#fff",
    color: "#111",
    borderRadius: 10,
    padding: "10px 14px",
    cursor: "pointer",
    minHeight: TAP,
  },
  btnPrimary: {
    border: "1px solid #2563eb",
    background: "#3b82f6",
    color: "#fff",
    borderRadius: 10,
    padding: "10px 14px",
    cursor: "pointer",
    minHeight: TAP,
  },
  btnBlue: {
    border: "1px solid #2563eb",
    background: "#3b82f6",
    color: "#fff",
    borderRadius: 10,
    padding: "10px 14px",
    cursor: "pointer",
    minHeight: TAP,
  },
  btnGreen: {
    border: "1px solid #16a34a",
    background: "#22c55e",
    color: "#fff",
    borderRadius: 10,
    padding: "10px 14px",
    cursor: "pointer",
    minHeight: TAP,
  },
  btnAmber: {
    border: "1px solid #d97706",
    background: "#f59e0b",
    color: "#fff",
    borderRadius: 10,
    padding: "10px 14px",
    cursor: "pointer",
    minHeight: TAP,
  },
  btnPurple: {
    border: "1px solid #7c3aed",
    background: "#a855f7",
    color: "#fff",
    borderRadius: 10,
    padding: "10px 14px",
    cursor: "pointer",
    minHeight: TAP,
  },
  btnIndigo: {
    border: "1px solid #4338ca",
    background: "#6366f1",
    color: "#fff",
    borderRadius: 10,
    padding: "10px 14px",
    cursor: "pointer",
    minHeight: TAP,
  },
  btnWarn: {
    border: "1px solid #fbbf24",
    background: "#fef3c7",
    color: "#92400e",
    borderRadius: 10,
    padding: "10px 14px",
    cursor: "pointer",
    minHeight: TAP,
  },
  btnDanger: {
    border: "1px solid #fecaca",
    background: "#ef4444",
    color: "#fff",
    borderRadius: 10,
    padding: "10px 14px",
    cursor: "pointer",
    minHeight: TAP,
  },
  btnLink: {
    border: "1px dashed #94a3b8",
    background: "#fff",
    color: "#111827",
    borderRadius: 10,
    padding: "10px 14px",
    cursor: "pointer",
    minHeight: TAP,
  },

  inputWrap: { display: "grid", gap: 6 },
  inputLabel: { fontSize: 14, color: "#374151", fontWeight: 600 },
  input: {
    width: "100%",
    minHeight: TAP,
    padding: "10px 12px",
    border: "1px solid #cbd5e1",
    borderRadius: 10,
    background: "#fff",
    fontSize: 15,
    outline: "none",
  },

  pill: {
    border: "1px solid #dbeafe",
    background: "#eff6ff",
    color: "#1d4ed8",
    borderRadius: 999,
    padding: "4px 10px",
    fontSize: 13,
  },
  errPill: {
    border: "1px solid #fecaca",
    background: "#fee2e2",
    color: "#991b1b",
    borderRadius: 999,
    padding: "4px 10px",
    fontSize: 13,
  },

  badge: {
    background: "#eef7ff",
    color: "#175cd3",
    border: "1px solid #d6e8ff",
    borderRadius: 999,
    padding: "6px 10px",
    fontSize: 13,
    whiteSpace: "nowrap",
  },
  badgeSmall: {
    background: "#ede9fe",
    color: "#5b21b6",
    border: "1px solid #ddd6fe",
    borderRadius: 999,
    padding: "2px 8px",
    fontSize: 12,
    whiteSpace: "nowrap",
  },

  overduePill: {
    border: "1px solid #fecaca",
    background: "#fee2e2",
    color: "#991b1b",
    borderRadius: 999,
    padding: "2px 8px",
    fontSize: 12,
    lineHeight: 1.6,
  },

  statusPill: {
    border: "1px solid #e5e7eb",
    background: "#f9fafb",
    color: "#111827",
    borderRadius: 999,
    padding: "4px 10px",
    fontSize: 13,
  },
  cardCancelled: {
    background: "#fff7f7",
    borderColor: "#fecaca",
    opacity: 0.66,
  },

  cancelBadge: {
    border: "1px solid #fecaca",
    background: "#fee2e2",
    color: "#991b1b",
    borderRadius: 8,
    padding: "2px 8px",
    fontSize: 13,
  },

  subtle: { fontSize: 13, color: "#6b7280" },
  label: { fontSize: 13.5, color: "#4b5563" },
  value: { fontSize: 15, fontWeight: 600 },
  chip: {
    border: "1px solid #ddd",
    borderRadius: 999,
    padding: "4px 10px",
    fontSize: 13,
    background: "#fff",
  },
  section: {
    border: "1px solid #eee",
    borderRadius: 12,
    padding: 12,
    marginTop: 10,
  },

  rowCard: {
    display: "grid",
    gridTemplateColumns:
      "minmax(64px,90px) 1.2fr 1fr 1fr minmax(120px,160px) auto",
    gap: 10,
    padding: 10,
    border: "1px solid #e5e7eb",
    borderRadius: 10,
    background: "#fff",
  },
  empty: { padding: 12, color: "#666", fontStyle: "italic" },
  loadingRow: { padding: 10, textAlign: "center", color: "#555" },
  endRow: { padding: 10, textAlign: "center", color: "#888" },

  insertMarker: {
    height: 0,
    margin: "0",
    borderTop: "3px solid #60a5fa",
    borderRadius: 2,
    pointerEvents: "none",
  },
  dragOriginShadow: {
    opacity: 0.2,
    boxShadow: "inset 0 0 0 2px #93c5fd",
    background: "#eff6ff",
  },
  dragOriginHidden: { visibility: "hidden" },
  dragOriginCollapse: {
    height: 0,
    margin: 0,
    padding: 0,
    border: 0,
    opacity: 0,
    overflow: "hidden",
  },
  dropPreviewCard: {
    opacity: 0.9,
    border: "2px dashed #60a5fa",
    background: "#f8fafc",
  },
  dropGhost: {
    border: "2px dashed #60a5fa",
    background: "rgba(96,165,250,0.08)",
    borderRadius: 10,
    margin: "20px 0",
    minHeight: 60,
    pointerEvents: "none",
    // transition: "height .08s ease"
  },
};
const modal = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,.45)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 9999,
  },
  card: {
    background: "#fff",
    borderRadius: 14,
    padding: 20,
    width: "min(860px,96vw)",
    maxHeight: "90vh",
    overflow: "auto",
  },
  cardFixed: {
    background: "#fff",
    borderRadius: 14,
    width: "min(1000px, 96vw)",
    height: "min(84vh, 860px)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    padding: 0,
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "16px 20px",
    borderBottom: "1px solid #eee",
  },
  content: { padding: 20, overflow: "auto", flex: "1 1 0" },
  footer: {
    padding: "12px 20px",
    borderTop: "1px solid #eee",
    display: "flex",
    gap: 10,
    justifyContent: "flex-end",
  },
};
const tabs = {
  wrap: {
    display: "flex",
    gap: 8,
    padding: "8px 12px",
    borderBottom: "1px solid #eee",
  },
  tab: {
    border: "1px solid #cbd5e1",
    background: "#fff",
    borderRadius: 999,
    padding: "8px 12px",
    fontSize: 14,
    cursor: "pointer",
    minHeight: TAP / 1.5,
  },
  active: { background: "#eef7ff", borderColor: "#c7e1ff", color: "#175cd3" },
};
const chatStyles = {
  box: {
    border: "1px solid #eee",
    borderRadius: 10,
    padding: 10,
    maxHeight: 300,
    overflowY: "auto",
    background: "#fff",
  },
  msg: { borderBottom: "1px dashed #eee", padding: "8px 2px" },
  meta: { fontSize: 13, color: "#666" },
};
const capStyles = {
  track: {
    width: "100%",
    height: 10,
    background: "#e5e7eb",
    borderRadius: 999,
    overflow: "hidden",
  },
  fill: { height: "100%", transition: "width .2s ease" },
};

function computeIndexFromPointer(listEl, y, draggingId, prevIndex) {
  // All cards except the one being dragged
  const items = Array.from(
    listEl.querySelectorAll("article[data-cardid]")
  ).filter((el) => el.getAttribute("data-cardid") !== draggingId);

  // No cards → drop at end
  if (!items.length) return 0;

  // Hysteresis: 15–25% of card height (min 8px)
  const deadZoneFor = (rect) => Math.max(8, rect.height * 0.22);

  // Default: end of list
  let fallback = items.length;

  for (let i = 0; i < items.length; i++) {
    const r = items[i].getBoundingClientRect();
    const mid = r.top + r.height / 2;
    const dz = deadZoneFor(r);

    if (y < mid - dz) return i; // definitely before
    if (y > mid + dz) {
      // definitely after
      fallback = i + 1;
      continue;
    }

    // Inside dead-zone: hold previous index if it points near this region
    if (typeof prevIndex === "number" && prevIndex >= 0) return prevIndex;

    // Otherwise, stay stable by snapping to current i
    return i;
  }
  return fallback;
}
